import * as React from "react";
import { Alert, Box, Button, Modal, SpaceBetween } from "@amzn/awsui-components-react";

export interface ConfirmActionModalProps {
    /** Whether the modal is visible */
    visible: boolean;
    /** Modal header text */
    header: string;
    /** Main message to display */
    message: string;
    /** Optional secondary description */
    description?: string;
    /** Alert type — "warning" for destructive actions, "info" for informational */
    alertType?: "warning" | "info" | "error";
    /** Label for the confirm button (default: "Confirm") */
    confirmLabel?: string;
    /** Label for the cancel button (default: "Cancel") */
    cancelLabel?: string;
    /** Called when user confirms the action */
    onConfirm: () => void;
    /** Called when user cancels or dismisses the modal */
    onCancel: () => void;
}

/**
 * Cloudscape-styled confirmation modal for destructive actions.
 * Replaces window.confirm for consistent UI styling.
 *
 * Usage:
 * ```tsx
 * <ConfirmActionModal
 *     visible={showConfirm}
 *     header="Delete Node"
 *     message="This will permanently remove the node."
 *     alertType="warning"
 *     confirmLabel="Delete"
 *     onConfirm={() => { doDelete(); setShowConfirm(false); }}
 *     onCancel={() => setShowConfirm(false)}
 * />
 * ```
 */
export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
    visible,
    header,
    message,
    description,
    alertType = "warning",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    onConfirm,
    onCancel
}) => {
    return (
        <Modal
            visible={visible}
            header={header}
            closeAriaLabel="Close"
            onDismiss={onCancel}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onCancel}>
                            {cancelLabel}
                        </Button>
                        <Button variant="primary" onClick={onConfirm}>
                            {confirmLabel}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="s">
                <Alert type={alertType}>
                    {message}
                </Alert>
                {description && (
                    <Box color="text-body-secondary">{description}</Box>
                )}
            </SpaceBetween>
        </Modal>
    );
};
